import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Landmark,
  Compass,
  Award,
  Building,
  ShieldCheck,
  HelpCircle,
  MapPin,
  Clock,
  Globe,
  ChevronDown,
  Sun,
  Moon,
  LogIn
} from "lucide-react";
import LogoIndak from "../../assets/img/logo-pdf.png";
import ImageCargoPlaneAI from "../../assets/img/cargo_plane_ai.png";
import { getAutoTheme } from "../../utils/themeHelper";
import "../home/home.css";

export default function About() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(getAutoTheme);
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;

    // Scroll Reveal Intersection Observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-scale").forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [lang]);

  // Sync theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("landing-theme", newTheme);
  };

  const changeLanguage = (newLang) => {
    setLang(newLang);
    localStorage.setItem("lang", newLang);
    setLangOpen(false);
  };

  const text = {
    en: {
      navHome: "Home",
      navAbout: "About",
      navServices: "Services",
      navContact: "Contact",
      navCheckRates: "Check Rates",
      navSignIn: "Sign In",
      navDashboard: "Dashboard",
      badgeAbout: "About INDAK",
      heroTitle: "Connecting Indonesia & Middle East",
      heroSubtitle: "We are committed to providing integrated international logistics and financial pathways that make it easy to send goods and transfer money directly from Indonesia to key destinations in Yemen and Saudi Arabia.",
      stat1Number: "150+",
      stat1Title: "Fast Service",
      stat1Desc: "Scheduled and fast shipping to major cities in the Middle East.",
      stat2Number: "15",
      stat2Title: "Trusted Worldwide",
      stat2Desc: "Trusted by thousands of customers for safe and reliable delivery.",
      stat3Number: "24/7",
      stat3Title: "Operational Support",
      stat3Desc: "Our team monitors your shipment status from origin to destination.",
      galleryBadge: "Our Operations",
      galleryTitle: "Indak Logistics Gallery",
      galleryDesc: "A glimpse of how our professional handling, fleet, and warehousing are managed for the safety of your shipment.",
      gal1Title: "Warehouse Hub",
      gal1Desc: "Neat and secure processing center in South Jakarta.",
      gal2Title: "Air Cargo Delivery",
      gal2Desc: "Direct air cargo services to destination airports in the Middle East.",
      gal3Title: "Money Transfer Network",
      gal3Desc: "Guaranteed, secure, and instant international money transfer network.",
      gal4Title: "Secure Packaging",
      gal4Desc: "Multi-layered robust packaging to prevent physical damage during transit.",
      gal5Title: "Customs Processing",
      gal5Desc: "Expert customs document processing team for smooth clearance abroad.",
      gal6Title: "Destination Courier",
      gal6Desc: "Trusted local couriers delivering directly to the recipient's address.",
      hubBadge: "Office Location",
      hubTitle: "Our Jakarta Hub",
      hubDesc: "All your packages are collected, recorded, and packaged professionally at our South Jakarta Hub before being shipped abroad.",
      hubAddressTitle: "Office Address",
      hubAddressValue: "Jl. Bango 3 no. 31, Cilandak, Jakarta Selatan, Indonesia",
      hubHoursTitle: "Operational Hours",
      hubHoursValue1: "Monday - Friday: 09:00 - 18:00 WIB",
      hubHoursValue2: "Saturday - Sunday: Closed (Except Appointment)",
      footerDesc: "Trusted international logistics and remittance solutions from Indonesia to Yemen and Saudi Arabia.",
      footerCopy: "All rights reserved."
    },
    ar: {
      navHome: "الرئيسية",
      navAbout: "من نحن",
      navServices: "خدماتنا",
      navContact: "اتصل بنا",
      navCheckRates: "أسعار الشحن",
      navSignIn: "تسجيل الدخول",
      navDashboard: "لوحة التحكم",
      badgeAbout: "حول إنداك",
      heroTitle: "ربط إندونيسيا والشرق الأوسط",
      heroSubtitle: "نحن ملتزمون بتوفير مسارات لوجستية ومالية دولية متكاملة تسهل شحن البضائع وتحويل الأموال مباشرة من إندونيسيا إلى وجهات رئيسية في اليمن والمملكة العربية السعودية.",
      stat1Number: "١٥٠+",
      stat1Title: "شحن سريع",
      stat1Desc: "شحن مجدول وسريع إلى المدن الرئيسية في الشرق الأوسط.",
      stat2Number: "١٥",
      stat2Title: "ثقة عالمية",
      stat2Desc: "موثوق من قبل آلاف العملاء لتقديم شحن آمن وموثوق.",
      stat3Number: "٢٤/٧",
      stat3Title: "دعم العمليات",
      stat3Desc: "يقوم فريقنا بمراقبة حالة شحنتك من المصدر إلى الوجهة النهائية.",
      galleryBadge: "عملياتنا",
      galleryTitle: "معرض إنداك اللوجستي",
      galleryDesc: "لمحة عن كيفية إدارة خدماتنا المهنية، والأسطول، والمستودعات لضمان سلامة شحنتك.",
      gal1Title: "مركز المستودعات",
      gal1Desc: "مركز معالجة مرتب وآمن في جنوب جاكرتا.",
      gal2Title: "شحن جوي",
      gal2Desc: "خدمات شحن جوي مباشر إلى مطارات الوجهة في الشرق الأوسط.",
      gal3Title: "شبكة تحويل الأموال",
      gal3Desc: "شبكة تحويل أموال دولية مضمونة وآمنة وفورية.",
      gal4Title: "تعبئة آمنة",
      gal4Desc: "تغليف متين متعدد الطبقات لمنع الأضرار المادية أثناء النقل.",
      gal5Title: "التخليص الجمركي",
      gal5Desc: "فريق خبراء لتجهيز مستندات الجمارك لتسهيل التخليص في الخارج.",
      gal6Title: "التسليم النهائي",
      gal6Desc: "موزعون محليون موثوقون يسلمون الشحنة مباشرة إلى عنوان المستلم.",
      hubBadge: "موقع المكتب",
      hubTitle: "مكتبنا في جاكرتا",
      hubDesc: "يتم تجميع وتسجيل وتغليف جميع الطرود باحترافية في مركزنا بجنوب جاكرتا قبل شحنها للخارج.",
      hubAddressTitle: "عنوان المكتب",
      hubAddressValue: "شارع بانغو ٣ رقم ٣١، سيلانداك، جنوب جاكرتا، إندونيسيا",
      hubHoursTitle: "ساعات العمل",
      hubHoursValue1: "الاثنين - الجمعة: 09:00 - 18:00 بتوقيت إندونيسيا",
      hubHoursValue2: "السبت - الأحد: مغلق (إلا بموعد مسبق)",
      footerDesc: "حلول لوجستية وحوالات مالية دولية موثوقة من إندونيسيا إلى اليمن والمملكة العربية السعودية.",
      footerCopy: "جميع الحقوق محفوظة."
    },
    id: {
      navHome: "Beranda",
      navAbout: "Tentang Kami",
      navServices: "Layanan",
      navContact: "Kontak",
      navCheckRates: "Cek Ongkir",
      navSignIn: "Masuk",
      navDashboard: "Dashboard",
      badgeAbout: "Visi & Misi",
      heroTitle: "Tentang INDAK",
      heroSubtitle: "Perusahaan pengiriman kargo dan transfer uang terpercaya yang menghubungkan Indonesia secara langsung dengan Yaman dan Arab Saudi.",
      storyBadge: "Perjalanan Kami",
      storyTitle: "Menghubungkan Jarak dan Membangun Kepercayaan",
      storyP1: "Didirikan dengan visi untuk mempermudah jalur pengiriman logistik antara Indonesia dan Timur Tengah, INDAK telah tumbuh menjadi salah satu penyedia layanan kargo dan pengiriman dana paling andal untuk komoditas, barang pribadi, dan remitansi.",
      storyP2: "Kami memahami pentingnya setiap paket dan setiap transaksi. Baik Anda mengirimkan hadiah untuk keluarga atau menjalankan bisnis impor/ekspor antar negara, tim kami memastikan penanganan aman dan tepat waktu.",
      missionTitle: "Misi Utama",
      missionDesc: "Menyediakan solusi logistik cepat, aman, dan efisien dengan standar keamanan tinggi untuk setiap pelanggan.",
      visionTitle: "Visi Masa Depan",
      visionDesc: "Menjadi mitra logistik utama pilihan di Timur Tengah yang menghubungkan Nusantara dengan dunia internasional secara terintegrasi.",
      hubBadge: "Lokasi Kantor",
      hubTitle: "Pusat Operasional Jakarta",
      hubDesc: "Seluruh paket diproses, ditimbang, dan dikemas secara profesional di hub kami di Jakarta Selatan sebelum diberangkatkan.",
      hubAddressTitle: "Alamat Kantor",
      hubAddressValue: "Jl. Bango 3 no. 31, Cilandak, Jakarta Selatan, Indonesia",
      hubHoursTitle: "Jam Operasional",
      hubHoursValue1: "Senin - Jumat: 09:00 - 18:00 WIB",
      hubHoursValue2: "Sabtu - Minggu: Tutup (Kecuali Janji Temu)",
      footerDesc: "Solusi logistik dan transfer uang internasional terpercaya dari Indonesia ke Yaman dan Arab Saudi.",
      footerCopy: "Hak cipta dilindungi undang-undang."
    }
  };

  const t = text[lang] || text.en;

  return (
    <div className="landing-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* HEADER NAVBAR */}
      <header className="landing-header">
        <div className="header-container">
          <div className="header-logo" onClick={() => navigate("/")}>
            <img src={LogoIndak} alt="Indak Logo" className="logo-img" />
          </div>

          <nav className="desktop-nav">
            <button onClick={() => navigate("/")} className="nav-link">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="nav-link active-link-highlight">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="nav-link">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="nav-link">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="nav-link">{t.navCheckRates}</button>

            {/* Language Switcher */}
            <div className="lang-dropdown-container">
              <button className="lang-dropdown-trigger" onClick={() => setLangOpen(!langOpen)}>
                <Globe size={16} />
                <span>{lang === "en" ? "EN" : lang === "id" ? "ID" : "AR"}</span>
                <ChevronDown size={14} />
              </button>
              {langOpen && (
                <div className="lang-dropdown-menu">
                  <button className={`lang-dropdown-item ${lang === "en" ? "active-lang" : ""}`} onClick={() => changeLanguage("en")}>
                    English
                  </button>
                  <button className={`lang-dropdown-item ${lang === "id" ? "active-lang" : ""}`} onClick={() => changeLanguage("id")}>
                    Bahasa Indonesia
                  </button>
                  <button className={`lang-dropdown-item ${lang === "ar" ? "active-lang" : ""}`} onClick={() => changeLanguage("ar")}>
                    العربية
                  </button>
                </div>
              )}
            </div>

            {/* Theme Toggle Button */}
            <button onClick={toggleTheme} className="theme-toggle-btn" title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}>
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>

            {/* Login Door Icon Button */}
            <button
              onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")}
              className="nav-sig-button"
              title={isLoggedIn ? t.navDashboard : t.navSignIn}
            >
              <LogIn size={20} />
            </button>
          </nav>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-nav">
            <button onClick={() => navigate("/")} className="mobile-nav-link">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="mobile-nav-link">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="mobile-nav-link">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="mobile-nav-link">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="mobile-nav-link">{t.navCheckRates}</button>

            <div style={{ display: "flex", gap: "20px", padding: "12px 0", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => changeLanguage("en")}
                  style={{ background: "none", border: "none", color: lang === "en" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  EN
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("id")}
                  style={{ background: "none", border: "none", color: lang === "id" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  ID
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("ar")}
                  style={{ background: "none", border: "none", color: lang === "ar" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  AR
                </button>
              </div>

              <button
                onClick={toggleTheme}
                style={{ background: "none", border: "none", color: "var(--header-link)", cursor: "pointer", padding: "4px" }}
              >
                {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
              </button>
            </div>

            <button onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")} className="mobile-nav-sig-cta">
              <LogIn size={18} /> {isLoggedIn ? t.navDashboard : t.navSignIn}
            </button>
          </div>
        )}
      </header>

      {/* ABOUT INTRO HERO */}
      <section className="inner-hero-section">
        <div className="section-container">
          <div className="inner-hero-grid">
            <div className="hero-content reveal-left">
              <span className="section-badge">{t.badgeAbout}</span>
              <h1 className="hero-title">
                {lang === "ar" ? (
                  <>
                    ربط إندونيسيا <span className="text-gradient">والشرق الأوسط</span>
                  </>
                ) : (
                  <>
                    Connecting Indonesia <br />
                    <span className="text-gradient">& Middle East</span>
                  </>
                )}
              </h1>
              <p className="hero-subtitle">
                {t.heroSubtitle}
              </p>
            </div>
            <div className="hero-visual-box reveal-right">
              <img src={ImageCargoPlaneAI} alt="Indak Courier cargo" className="inner-hero-img" />
            </div>
          </div>
        </div>
      </section>

      {/* STATS & METRICS (With Motion Reveal) */}
      <section className="about-stats-section reveal">
        <div className="section-container">
          <div className="about-stats-grid">
            <div className="about-stat-card reveal-scale reveal-delay-1">
              <h3>{t.stat1Number}</h3>
              <h4>{t.stat1Title}</h4>
              <p>{t.stat1Desc}</p>
            </div>
            <div className="about-stat-card reveal-scale reveal-delay-2">
              <h3>{t.stat2Number}</h3>
              <h4>{t.stat2Title}</h4>
              <p>{t.stat2Desc}</p>
            </div>
            <div className="about-stat-card reveal-scale reveal-delay-3">
              <h3>{t.stat3Number}</h3>
              <h4>{t.stat3Title}</h4>
              <p>{t.stat3Desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY GRID (With Motion Reveal) */}
      <section className="about-gallery-section reveal">
        <div className="section-container">
          <div className="section-header-center">
            <span className="section-badge">{t.galleryBadge}</span>
            <h2 className="section-title">{t.galleryTitle}</h2>
            <p className="section-desc-center">
              {t.galleryDesc}
            </p>
          </div>

          <div className="gallery-grid">
            <div className="gallery-card reveal-scale reveal-delay-1">
              <div className="gallery-icon-box"><Building size={24} /></div>
              <h4>{t.gal1Title}</h4>
              <p>{t.gal1Desc}</p>
            </div>
            <div className="gallery-card reveal-scale reveal-delay-2">
              <div className="gallery-icon-box"><Compass size={24} /></div>
              <h4>{t.gal2Title}</h4>
              <p>{t.gal2Desc}</p>
            </div>
            <div className="gallery-card reveal-scale reveal-delay-3">
              <div className="gallery-icon-box"><Landmark size={24} /></div>
              <h4>{t.gal3Title}</h4>
              <p>{t.gal3Desc}</p>
            </div>
            <div className="gallery-card reveal-scale reveal-delay-1">
              <div className="gallery-icon-box"><ShieldCheck size={24} /></div>
              <h4>{t.gal4Title}</h4>
              <p>{t.gal4Desc}</p>
            </div>
            <div className="gallery-card reveal-scale reveal-delay-2">
              <div className="gallery-icon-box"><Award size={24} /></div>
              <h4>{t.gal5Title}</h4>
              <p>{t.gal5Desc}</p>
            </div>
            <div className="gallery-card reveal-scale reveal-delay-3">
              <div className="gallery-icon-box"><HelpCircle size={24} /></div>
              <h4>{t.gal6Title}</h4>
              <p>{t.gal6Desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* HUB LOCATION DETAILS (With Motion Reveal) */}
      <section className="about-hub-section">
        <div className="section-container">
          <div className="hub-details-card">
            <div className="hub-grid">
              <div className="hub-info-text reveal-left">
                <span className="section-badge">{t.hubBadge}</span>
                <h3 className="section-title">{t.hubTitle}</h3>
                <p className="section-desc">
                  {t.hubDesc}
                </p>
                <div className="hub-address-box">
                  <MapPin className="text-danger" size={20} />
                  <div>
                    <h4>{t.hubAddressTitle}</h4>
                    <p>{t.hubAddressValue}</p>
                  </div>
                </div>
                <div className="hub-hours-box">
                  <Clock className="text-danger" size={20} />
                  <div>
                    <h4>{t.hubHoursTitle}</h4>
                    <p>{t.hubHoursValue1}</p>
                    <p>{t.hubHoursValue2}</p>
                  </div>
                </div>
              </div>
              <div className="hub-info-visual reveal-right">
                <div className="hub-map-placeholder">
                  <iframe
                    title="Jakarta Hub Office Map"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3965.7876771343714!2d106.7997977!3d-6.2916053!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e69f1e1a533bfad%3A0xc6cb1b58f8b3c6dd!2sJl.%20Bango%20III%20No.31%2C%20RT.10%2FRW.3%2C%20Cilandak%20Bar.%2C%20Kec.%20Cilandak%2C%20Kota%20Jakarta%20Selatan%2C%20Daerah%20Khusus%20Ibukota%20Jakarta%2012430!5e0!3m2!1sid!2sid!4v1786262144619!5m2!1sid!2sid"
                    width="100%"
                    height="100%"
                    style={{ border: 0, borderRadius: "14px" }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-main-grid">
            <div className="footer-col footer-col-brand">
              <p className="footer-brand-desc">{t.footerDesc}</p>
            </div>

            <div className="footer-col footer-col-links">
              <h4 className="footer-col-title">{lang === "ar" ? "روابط سريعة" : lang === "id" ? "Navigasi Cepat" : "Quick Links"}</h4>
              <ul className="footer-links-list">
                <li><button onClick={() => navigate("/")}>{t.navHome}</button></li>
                <li><button onClick={() => navigate("/about")}>{t.navAbout}</button></li>
                <li><button onClick={() => navigate("/services")}>{t.navServices}</button></li>
                <li><button onClick={() => navigate("/check-rates")}>{t.navCheckRates}</button></li>
                <li><button onClick={() => navigate("/contact")}>{t.navContact}</button></li>
              </ul>
            </div>

            <div className="footer-col footer-col-services">
              <h4 className="footer-col-title">{lang === "ar" ? "وجهات الخدمة" : lang === "id" ? "Cakupan Destinasi" : "Service Coverage"}</h4>
              <ul className="footer-links-list text-muted-list">
                <li>🇾🇪 Yemen (Tarim, Mukalla, Aden, Sana'a)</li>
                <li>🇸🇦 Saudi Arabia (Jeddah, Riyadh, Makkah)</li>
                <li>📦 Air & Sea Cargo Shipping</li>
                <li>💵 Outbound & Inbound Money Transfer</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="copyright-text">
              &copy; {new Date().getFullYear()} INDAK. {t.footerCopy}
            </p>
            {/* <div className="dev-tag">
              <span className="dev-by">{lang === "ar" ? "تطوير بواسطة" : "Dev By"}</span>
              <span className="dev-name">~ YallaBob ~</span>
            </div> */}
          </div>
        </div>
      </footer>
    </div>
  );
}
